import { useState, useMemo, useEffect } from 'react';
import { classes } from '../data/classes';
import { DND_CLASSES } from '../types';
import type { DndClass, ClassFeature } from '../types';
import { ClassCard } from '../components/ClassCard';
import { CardListPage, type CardListPageConfig } from '../components/CardListPage';
import { useImport } from '../context/useImport';
import './ClassesPage.css';

type SortOption = 'level' | 'name' | 'class';

// Get unique subclasses from classes
function getSubclasses(allClasses: ClassFeature[], className: string | 'All'): string[] {
  const subclasses = new Set<string>();
  allClasses.forEach(f => {
    if (f.subclass && (className === 'All' || f.className === className)) {
      subclasses.add(f.subclass);
    }
  });
  return [...subclasses].sort();
}

// Wrapper components to adapt entity prop to classFeature prop
function ClassListCard({ entity, isSelected, onToggle }: { entity: ClassFeature; isSelected: boolean; onToggle: (id: string) => void }) {
  return <ClassCard classFeature={entity} isSelected={isSelected} onToggle={onToggle} />;
}

export function ClassesPage() {
  const [selectedClass, setSelectedClass] = useState<DndClass | 'All'>('All');
  const [selectedSubclass, setSelectedSubclass] = useState<string>('All');
  const [minLevel, setMinLevel] = useState(1);
  const [maxLevel, setMaxLevel] = useState(20);
  const [sortBy, setSortBy] = useState<SortOption>('class');
  const [importedOnly, setImportedOnly] = useState(false);

  const {
    customSpells,
    customItems,
    customClasses,
    customFeats,
    multiTabImportState,
    importMultiTab,
    importMultipleUrls,
    clearMultiTabImportStatus,
    clearCustomSpells,
    clearCustomItems,
    clearCustomClasses,
    clearCustomFeats,
    combinedTemplateUrl,
  } = useImport();

  // Reset subclass when class changes
  useEffect(() => {
    setSelectedSubclass('All');
  }, [selectedClass]);

  // Combine base classes with custom classes
  const allClasses = useMemo(() => [...classes, ...customClasses], [customClasses]);
  const visibleClasses = useMemo(
    () => (importedOnly ? customClasses : allClasses),
    [importedOnly, customClasses, allClasses]
  );

  // Get available subclasses for current class filter
  const availableSubclasses = useMemo(() => getSubclasses(visibleClasses, selectedClass), [visibleClasses, selectedClass]);

  const filteredClasses = useMemo(() => {
    return visibleClasses
      .filter((classFeature) => {
        // Class filter
        if (selectedClass !== 'All' && classFeature.className !== selectedClass) {
          return false;
        }
        // Subclass filter
        if (selectedSubclass !== 'All') {
          if (selectedSubclass === 'Core') {
            // Show only core class features (no subclass)
            if (classFeature.subclass) return false;
          } else {
            // Show only class features from the selected subclass
            if (classFeature.subclass !== selectedSubclass) return false;
          }
        }
        // Level filter
        if (classFeature.level < minLevel || classFeature.level > maxLevel) {
          return false;
        }
        return true;
      })
      .sort((a, b) => {
        if (sortBy === 'level') {
          if (a.level !== b.level) return a.level - b.level;
          return a.name.localeCompare(b.name);
        }
        if (sortBy === 'class') {
          if (a.className !== b.className) return a.className.localeCompare(b.className);
          if (a.level !== b.level) return a.level - b.level;
          return a.name.localeCompare(b.name);
        }
        return a.name.localeCompare(b.name);
      });
  }, [selectedClass, selectedSubclass, minLevel, maxLevel, sortBy, visibleClasses]);

  const renderFilters = () => (
    <>
      <div className="filter-row">
        <div className="filter-group">
          <label>CLASS:</label>
          <select
            value={selectedClass}
            onChange={(e) => setSelectedClass(e.target.value as DndClass | 'All')}
            className="filter-select"
          >
            <option value="All">All Classes</option>
            {DND_CLASSES.map((cls) => (
              <option key={cls} value={cls}>
                {cls}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>SUBCLASS:</label>
          <select
            value={selectedSubclass}
            onChange={(e) => setSelectedSubclass(e.target.value)}
            className="filter-select"
          >
            <option value="All">All</option>
            <option value="Core">Core Only</option>
            {availableSubclasses.map((sub) => (
              <option key={sub} value={sub}>
                {sub}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="filter-row">
        <div className="filter-group">
          <label>LEVEL:</label>
          <div className="level-range">
            <input
              type="number"
              min={1}
              max={20}
              value={minLevel}
              onChange={(e) => setMinLevel(Number(e.target.value))}
              className="level-input"
            />
            <span>–</span>
            <input
              type="number"
              min={1}
              max={20}
              value={maxLevel}
              onChange={(e) => setMaxLevel(Number(e.target.value))}
              className="level-input"
            />
          </div>
        </div>

        <div className="filter-group">
          <label>SORT:</label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortOption)}
            className="filter-select"
          >
            <option value="level">Level</option>
            <option value="name">A-Z</option>
            <option value="class">Class</option>
          </select>
        </div>
      </div>

      <div className="filter-row">
        <div className="filter-group imported-only-group">
          <label htmlFor="classes-imported-only" className="imported-only-label">
            <input
              id="classes-imported-only"
              type="checkbox"
              checked={importedOnly}
              onChange={(e) => setImportedOnly(e.target.checked)}
              className="imported-only-checkbox"
            />
            <span>IMPORTED ONLY</span>
          </label>
        </div>
      </div>
    </>
  );

  const config: CardListPageConfig<ClassFeature> = {
    variant: 'classes',
    pageClassName: 'classes-page',
    listClassName: 'classes-list',
    documentTitle: 'Class Feature Cards',
    searchPlaceholder: 'Search classes...',
    emptyState: {
      icon: '⚡',
      title: 'Class Feature Cards',
       description: 'Select classes from the list to preview them here',
      instructions: [
        '1. Use the filters to find classes by level or subclass',
        '2. Click on class features to add them to your selection',
        '3. Download your cards when ready to print',
      ],
    },
    import: {
      onImport: importMultiTab,
      onImportMultiple: importMultipleUrls,
      state: multiTabImportState,
      onClearStatus: clearMultiTabImportStatus,
      spellsCount: customSpells.length,
      itemsCount: customItems.length,
      classesCount: customClasses.length,
      featsCount: customFeats.length,
      onClearSpells: clearCustomSpells,
      onClearItems: clearCustomItems,
      onClearClasses: clearCustomClasses,
      onClearFeats: clearCustomFeats,
      combinedTemplateUrl,
    },
    ListCard: ClassListCard,
    renderFilters,
  };

  return (
      <CardListPage
      allEntities={visibleClasses}
      filteredEntities={filteredClasses}
      config={config}
    />
  );
}
